/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-08-24
 */

// Add third-party dependencies.
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Create the payrollSlice.
export const payrollSlice = createApi({
  reducerPath: 'payrollApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API,
    prepareHeaders: (headers) => {
      // Get the access token from localStorage.
      const token = localStorage.getItem('accessToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  // Add endpoints here.
  tagTypes: ['payroll', 'allpayrates'],
  endpoints: (builder) => ({
    getPayroll: builder.query({
      query: () => 'timesheet/payroll/',
      providesTags: ['payroll'],
    }),
    processActivities: builder.mutation({
      query: (body) => ({
        url: 'timesheet/process-activities/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['payroll'],
    }),
    getAllPayRates: builder.query({
      query: () => 'timesheet/allpayrates/',
      providesTags: ['allpayrates'],
    }),
    addPayRate: builder.mutation({
      query: (body) => ({
        url: 'timesheet/payrates/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['allpayrates'],
    }),
    updatePayRate: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `timesheet/payrates/${id}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['allpayrates'],
    }),
    deletePayRate: builder.mutation({
      query: (id) => ({
        url: `timesheet/payrates/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['allpayrates'],
    }),
    getUserActivities: builder.query({
      query: (userId) => `timesheet/activities/user/${userId}/`,
      providesTags: ['activities'],
    }),
    getUserPayRates: builder.query({
      query: (userId) => `timesheet/payrates/user/${userId}/`,
    }),
    getDeskActivities: builder.query({
      query: () => 'timesheet/desk-activities/',
    }),
    getAdminPay: builder.mutation({
      query: (body) => ({
        url: 'timesheet/admin-pay/',
        method: 'POST',
        body,
      }),
    }),
  }),
});

// Export the auto-generated hooks.
export const {
  useGetPayrollQuery,
  useProcessActivitiesMutation,
  useGetAllPayRatesQuery,
  useAddPayRateMutation,
  useUpdatePayRateMutation,
  useDeletePayRateMutation,
  useGetUserActivitiesQuery,
  useGetUserPayRatesQuery,
  useGetAdminPayMutation,
  useGetDeskActivitiesQuery,
} = payrollSlice;
